import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { knowledgeApi } from '@/api/endpoints';
import { Card } from '../components/ui';
import { sanitizeArticleHtml } from '@/pages/KnowledgeBase/sanitizeArticleHtml';

const PREVIEW_LIMIT = 800;

/**
 * FAQ в новом дизайне. Данные — статьи KB из категории slug='faq' (или
 * содержащей 'faq'), так же как в старом FaqPage. Никаких моков.
 */
export function FaqView() {
  const { data: categories } = useQuery({
    queryKey: ['knowledge-categories'],
    queryFn: knowledgeApi.getCategories,
    staleTime: 10 * 60 * 1000,
  });

  const faqCategory = useMemo(() => {
    const list = categories || [];
    const norm = (v?: string | null) => (v || '').toLowerCase();
    return (
      list.find((c) => norm(c.slug) === 'faq') ||
      list.find((c) => norm(c.slug).includes('faq')) ||
      list.find((c) => /faq|частые/.test(norm(c.name)))
    );
  }, [categories]);

  const { data: articlesPage } = useQuery({
    queryKey: ['faq-articles', faqCategory?.id],
    queryFn: () => knowledgeApi.getArticles({ categoryId: faqCategory!.id, limit: 100 }),
    enabled: !!faqCategory,
    staleTime: 5 * 60 * 1000,
  });

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpenIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const items = articlesPage?.data || [];

  return (
    <div className="view active">
      <div className="page-hero">
        <h2>Часто задаваемые вопросы</h2>
        <p>Короткие ответы на самые частые вопросы клиентов.</p>
      </div>
      <Card className="faq-card reference-faq-card">
        {items.length === 0 ? (
          <div className="empty" style={{ padding: 32, textAlign: 'center' }}>
            {faqCategory ? 'В FAQ пока пусто.' : 'Категория FAQ ещё не создана.'}
          </div>
        ) : (
          items.map((article) => {
            const isOpen = openIds.has(article.id);
            const html = sanitizeArticleHtml(article.excerpt || (article.content || '').slice(0, PREVIEW_LIMIT));
            return (
              <div className={`faq-item reference-faq-item ${isOpen ? 'open' : ''}`} key={article.id}>
                <button type="button" aria-expanded={isOpen} onClick={() => toggle(article.id)}>
                  <span className="faq-question">
                    <HelpCircle size={17} />
                    {article.title}
                  </span>
                  {isOpen ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
                </button>
                {isOpen ? (
                  <div className="faq-answer-content" dangerouslySetInnerHTML={{ __html: html }} />
                ) : null}
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}
